import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, MessageCircle, CheckCircle2, AlertCircle, Phone } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useSite, waLinkFromNumber, formatPhone } from "@/context/SiteContext";
import { usePageContent } from "@/context/PageContentContext";
import { buildContactMessage, openWhatsApp } from "@/lib/whatsappMessages";

export default function Contact() {
  const { content: site } = useSite();
  const { content } = usePageContent("contact");
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/leads/contact", form);
      openWhatsApp(site.whatsapp, buildContactMessage(form));
      setStatus({
        state: "success",
        msg: "Saved. WhatsApp opened — let's continue the chat there.",
      });
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <div data-testid="page-contact" className="bg-cream">
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-35 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="font-mono-label text-[var(--k-green)] mb-5">{content.hero_eyebrow}</div>
            <h1 data-testid="contact-headline" className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]">
              {content.hero_title_pre} <em className="green-gradient-text not-italic">{content.hero_title_em}</em>{content.hero_title_post}
            </h1>
            <p className="mt-6 text-lg text-[var(--k-text-2)] max-w-2xl leading-relaxed">
              {content.hero_intro}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-cream pt-0 md:pt-0" data-testid="contact-grid">
        <div className="container-k grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <OfficeCard label="Registered Office" value={site.noida_office} testId="contact-office-noida" />
            <OfficeCard label="Gurgaon Office" value={site.gurgaon_office} testId="contact-office-gurgaon" />
            <ContactRow
              icon={MessageCircle}
              label="WhatsApp"
              value={formatPhone(site.whatsapp)}
              href={waLinkFromNumber(site.whatsapp, "Hi KOSTRA — I'd like to connect.")}
              testId="contact-whatsapp"
              color="#25D366"
            />
            <ContactRow
              icon={Phone}
              label="Call"
              value={formatPhone(site.whatsapp)}
              href={`tel:${site.whatsapp}`}
              testId="contact-phone"
              color="var(--k-green)"
            />
            <ContactRow
              icon={Mail}
              label="Email"
              value={site.email}
              href={`mailto:${site.email}`}
              testId="contact-email"
              color="var(--k-green)"
            />
          </div>

          <form onSubmit={submit} className="lg:col-span-7 organic-card p-8 md:p-12" data-testid="contact-form">
            <div className="font-mono-label text-[var(--k-gold)] mb-2">{content.form_eyebrow}</div>
            <h2 className="font-serif-display text-3xl text-[var(--k-text)] mb-10">{content.form_title}</h2>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Name *">
                <input className="kostra-input" required value={form.name} onChange={update("name")} data-testid="contact-input-name" />
              </Field>
              <Field label="Mobile *">
                <input className="kostra-input" type="tel" required value={form.mobile} onChange={update("mobile")} placeholder="+91 99999 00000" data-testid="contact-input-mobile" />
              </Field>
              <Field label="Email *" full>
                <input className="kostra-input" type="email" required value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="contact-input-email" />
              </Field>
              <Field label="Message" full>
                <textarea className="kostra-input resize-none" rows={4} value={form.message} onChange={update("message")} placeholder="How can we help?" data-testid="contact-input-message" />
              </Field>
            </div>

            {status.state === "success" && (
              <div className="mt-8 flex items-start gap-3 border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)] p-4 rounded" data-testid="contact-form-success">
                <CheckCircle2 className="w-5 h-5 text-[var(--k-green)] mt-0.5" />
                <p className="text-[var(--k-text)] text-sm">{status.msg}</p>
              </div>
            )}
            {status.state === "error" && (
              <div className="mt-8 flex items-start gap-3 border border-red-300 bg-red-50 p-4 rounded" data-testid="contact-form-error">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <p className="text-[var(--k-text)] text-sm">{status.msg}</p>
              </div>
            )}

            <button type="submit" disabled={status.state === "loading"} className="btn-green mt-10" data-testid="contact-submit-btn">
              {status.state === "loading" ? "Sending…" : (<>Send to WhatsApp <MessageCircle className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function OfficeCard({ label, value, testId }) {
  return (
    <div className="organic-card p-6 flex gap-4" data-testid={testId}>
      <div className="w-11 h-11 rounded-full bg-[var(--k-bg-2)] border border-[var(--k-line)] flex items-center justify-center shrink-0">
        <MapPin className="w-5 h-5 text-[var(--k-green)]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem]">{label}</div>
        <div className="text-[var(--k-text)] mt-1.5">{value}</div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId, color }) {
  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testId} className="organic-card p-6 flex gap-4 items-center hover:!border-[var(--k-green)]">
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: color, opacity: 0.9 }}>
        <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem]">{label}</div>
        <div className="text-[var(--k-text)] mt-1.5">{value}</div>
      </div>
    </a>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}
